/**
 * Date utilities for Sri Lanka timezone (IST: UTC+5:30)
 */

const TIMEZONE_OFFSET_MS = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds

/**
 * Convert date to Sri Lanka timezone by adding UTC+5:30 offset
 * @param {Date|string} date - Date object or ISO string
 * @returns {Date} - Date adjusted to Sri Lanka timezone
 */
const convertToSriLankaTime = (date) => {
  if (!date) return null;
  
  const dateObj = typeof date === 'string' ? new Date(date) : new Date(date);
  
  // Get UTC time and add 5:30 hours offset for Sri Lanka
  const utcTime = dateObj.getTime();
  const sriLankaTime = new Date(utcTime + TIMEZONE_OFFSET_MS);
  
  return sriLankaTime;
};

/**
 * Format date in Sri Lanka timezone
 * @param {Date|string} dateString - Date to format
 * @param {string} format - 'date' | 'time' | 'datetime'
 * @returns {string} - Formatted date string
 */
export const formatDateSriLanka = (dateString, format = 'date') => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  const sriLankaDate = convertToSriLankaTime(date);
  
  switch (format) {
    case 'date':
      return sriLankaDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    
    case 'time':
      return sriLankaDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    
    case 'datetime':
      return sriLankaDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    
    default:
      return sriLankaDate.toLocaleDateString('en-US');
  }
};

/**
 * Get current time in Sri Lanka timezone
 * @param {string} format - 'date' | 'time' | 'datetime'
 * @returns {string} - Formatted current time
 */
export const getCurrentSriLankaTime = (format = 'datetime') => {
  return formatDateSriLanka(new Date(), format);
};

/**
 * Format date and time together
 * @param {Date|string} dateString - Date to format
 * @returns {string} - Formatted as "MMM DD, YYYY HH:MM AM/PM"
 */
export const formatDateTimeSriLanka = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  const sriLankaDate = convertToSriLankaTime(date);
  
  const dateStr = sriLankaDate.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });
  
  const timeStr = sriLankaDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  return `${dateStr} ${timeStr}`;
};

/**
 * Get Sri Lanka timezone offset (UTC+5:30)
 * @returns {string} - "UTC+5:30"
 */
export const getSriLankaTimezoneOffset = () => {
  return 'UTC+5:30 (IST)';
};
